import React, { forwardRef, useRef, useImperativeHandle, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import ToastUIImageEditor from '@toast-ui/react-image-editor';
import _isEmpty from 'lodash/isEmpty';
import _isNumber from 'lodash/isNumber';
import _last from 'lodash/last';

import {
  THEME_WHITE,
  THEME_BLACK,
  whiteTheme,
  blackTheme,
  CUSTOM_CROP_INPUT_CLASS_NAME,
  CROP_DRAWING_MODE,
  CROP_BUTTONS_SELECTOR,
} from './ImageEditor.constants';
import { createCustomCropWidgets } from './ImageEditor.utils';

// eslint-disable-next-line import/order, import/imports-first
import 'tui-image-editor/dist/tui-image-editor.css';

// eslint-disable-next-line import/order
import './ImageEditor.scss';

const propTypes = {
  imageUrl: PropTypes.string.isRequired,
  theme: PropTypes.oneOf([THEME_WHITE, THEME_BLACK]),
};
const defaultProps = {
  theme: THEME_WHITE,
};

const ImageEditor = forwardRef(function ImageEditor({ imageUrl, theme }, ref) {
  const imageEditorRef = useRef(null);
  const containerRef = useRef(null);

  const themeMap = theme === THEME_WHITE ? whiteTheme : blackTheme;

  const [drawingMode, setDrawingMode] = useState(null);

  function getInstance() {
    return imageEditorRef.current?.getInstance();
  }

  useImperativeHandle(
    ref,
    () => ({
      getImage: async (options) =>
        new Promise((resolve) => {
          setTimeout(() => {
            const dataUrl = getInstance().toDataURL(options);
            resolve(dataUrl);
          }, 0);
        }),
      loadImageFromURL: (url, filename) => {
        return getInstance().loadImageFromURL(url, filename);
      },
    }),
    [],
  );

  function applyCropRatio(ratio) {
    const instance = getInstance();

    if (_isNumber(ratio)) {
      instance.setCropzoneRect(ratio);
    }
  }

  useEffect(() => {
    let observer = null;
    const instance = getInstance();

    if (instance && containerRef.current) {
      observer = new MutationObserver(() => {
        if (instance && instance.ui) {
          setDrawingMode(instance.getDrawingMode());
        }
      });

      observer.observe(containerRef.current, {
        childList: true,
        attributes: true,
        subtree: true,
      });
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [containerRef, imageEditorRef]);

  function onCropModeChange() {
    const container = containerRef.current;
    const isCropping = drawingMode === CROP_DRAWING_MODE;

    const defaultCropButtons = container.querySelectorAll(CROP_BUTTONS_SELECTOR);
    const lastDefaultCropButton = _last(defaultCropButtons);
    const customCropButtons = container.querySelectorAll(`.${CUSTOM_CROP_INPUT_CLASS_NAME}`);
    const hasCustomCropButtons = !_isEmpty(customCropButtons);

    if (isCropping) {
      if (lastDefaultCropButton && !hasCustomCropButtons) {
        createCustomCropWidgets(lastDefaultCropButton, applyCropRatio);
      }
    } else if (hasCustomCropButtons) {
      customCropButtons.forEach((button) => {
        button.remove();
      });
    }
  }

  useEffect(() => {
    onCropModeChange();
  }, [drawingMode]);

  return (
    <div className={`image-editor-container ${theme}`} ref={containerRef}>
      <ToastUIImageEditor
        ref={imageEditorRef}
        usageStatistics={false}
        includeUI={{
          theme: themeMap,
          loadImage: {
            path: imageUrl,
            name: 'Image',
          },
          uiSize: {
            width: '100%',
            height: '100%',
          },
        }}
      />
    </div>
  );
});

export default ImageEditor;

ImageEditor.propTypes = propTypes;
ImageEditor.defaultProps = defaultProps;
